import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SummaryApi from "../../../common";


export default function IdCardManager() {
  const [idCards, setIdCards] = useState([]);
  const [schools, setSchools] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [useExistingSchool, setUseExistingSchool] = useState(false);
  const [selectedSchoolId, setSelectedSchoolId] = useState("");
  const [form, setForm] = useState({
    schoolName: "",
    contact1: "",
    contact2: "",
    projectName: "",
    amount: "",
    address: "",
  });
  const [editingId, setEditingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [message, setMessage] = useState("");

  const navigate = useNavigate();

  const fetchIdCards = async () => {
    try {
      const res = await fetch(SummaryApi.idcards.getAll.url);
      const data = await res.json();
      setIdCards(data);
    } catch (err) {
      console.error("Error fetching ID Cards:", err);
    }
  };

  const fetchSchools = async () => {
    try {
      const res = await fetch(SummaryApi.schools.getAll.url);
      const data = await res.json();
      setSchools(data);
    } catch (err) {
      console.error("Error fetching Schools:", err);
    }
  };

  const fetchTransactions = async () => {
    try {
      const res = await fetch(SummaryApi.idTransactions.getAll.url);
      const data = await res.json();
      setTransactions(data);
    } catch (err) {
      console.error("Error fetching transactions:", err);
    }
  };

  useEffect(() => {
    fetchIdCards();
    fetchSchools();
    fetchTransactions();
  }, []);

  // 🧮 Calculate paid amount for a specific ID card (based on installment status)
const getPaidAmount = (idCardId) => {
  const idCardTransactions = transactions.filter(
    (t) => t.idCardId?._id === idCardId
  );

  return idCardTransactions.reduce((total, transaction) => {
    const paidSum = transaction.installments
      ?.filter((inst) => inst.status === "Paid")
      .reduce((sum, inst) => sum + Number(inst.amount || 0), 0);
    return total + paidSum;
  }, 0);
};

// 🧮 Calculate pending amount for a specific ID card (based on installment status)
const getPendingAmount = (idCardId) => {
  const idCardTransactions = transactions.filter(
    (t) => t.idCardId?._id === idCardId
  );

  return idCardTransactions.reduce((total, transaction) => {
    const pendingSum = transaction.installments
      ?.filter((inst) => inst.status !== "Paid")
      .reduce((sum, inst) => sum + Number(inst.amount || 0), 0);
    return total + pendingSum;
  }, 0);
};


  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSchoolSelect = (e) => {
    const schoolId = e.target.value;
    setSelectedSchoolId(schoolId);

    if (schoolId) {
      const school = schools.find((s) => s._id === schoolId);
      if (school) {
        // Pre-fill form with school data, but keep it editable
        setForm({
          schoolName: school.schoolName,
          contact1: school.contact1,
          contact2: school.contact2 || "",
          projectName: school.projectName,
          amount: school.amount,
          address: school.address,
        });
      }
    } else {
      // Reset form if no school selected
      setForm({
        schoolName: "",
        contact1: "",
        contact2: "",
        projectName: "",
        amount: "",
        address: "",
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      let schoolIdToUse = selectedSchoolId;

      // If not using existing school, create a new school first
      if (!useExistingSchool || !selectedSchoolId) {
        const schoolRes = await fetch(SummaryApi.schools.create.url, {
          method: SummaryApi.schools.create.method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        });

        if (!schoolRes.ok) throw new Error("Failed to create school");

        const newSchool = await schoolRes.json();
        schoolIdToUse = newSchool._id;

        // Refresh schools list
        await fetchSchools();
      }

      // Now create or update the ID card with schoolId
      const idCardData = {
        ...form,
        schoolId: schoolIdToUse,
      };

      const api = editingId
        ? SummaryApi.idcards.update(editingId)
        : SummaryApi.idcards.create;

      const res = await fetch(api.url, {
        method: api.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(idCardData),
      });

      if (!res.ok) throw new Error("Request failed");

      setMessage(editingId ? "ID Card updated successfully!" : "ID Card created!");
      setShowModal(false);
      resetForm();
      fetchIdCards();
    } catch (error) {
      console.error("Error saving ID Card:", error);
      setMessage("Failed to save ID Card.");
    }
  };

  const resetForm = () => {
    setForm({
      schoolName: "",
      contact1: "",
      contact2: "",
      projectName: "",
      amount: "",
      address: "",
    });
    setEditingId(null);
    setUseExistingSchool(false);
    setSelectedSchoolId("");
  };

  const handleEdit = (idCard) => {
    setForm({
      schoolName: idCard.schoolName,
      contact1: idCard.contact1,
      contact2: idCard.contact2 || "",
      projectName: idCard.projectName,
      amount: idCard.amount,
      address: idCard.address,
    });
    setEditingId(idCard._id);
    setSelectedSchoolId(idCard.schoolId || "");
    setUseExistingSchool(!!idCard.schoolId);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this ID Card?")) return;

    try {
      const api = SummaryApi.idcards.delete(id);
      await fetch(api.url, { method: api.method });
      fetchIdCards();
    } catch (error) {
      console.error("Error deleting ID Card:", error);
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-800">ID Card Management</h1>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow"
        >
          + Create ID Card
        </button>
      </div>

      {/* Success Message */}
      {message && (
        <div className="bg-green-100 text-green-800 px-4 py-2 rounded mb-4 text-sm">
          {message}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full text-sm text-left text-gray-700">
          <thead className="bg-gray-100 text-gray-800 uppercase text-xs">
            <tr>
              <th className="px-4 py-3">#</th>
              <th className="px-4 py-3">School Name</th>
              <th className="px-4 py-3">Project</th>
              <th className="px-4 py-3">Total Amount</th>
              <th className="px-4 py-3">Paid Amount</th>
              <th className="px-4 py-3">Pending Amount</th>
              <th className="px-4 py-3">Contacts</th>
              <th className="px-4 py-3">Address</th>
              <th className="px-4 py-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {idCards.length ? (
              idCards.map((card, idx) => {
                const paidAmount = getPaidAmount(card._id);
                const pendingAmount = getPendingAmount(card._id);
                
                return (
                  <tr key={card._id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3">{idx + 1}</td>
                    <td className="px-4 py-3 font-medium">{card.schoolName}</td>
                    <td className="px-4 py-3">{card.projectName}</td>
                    <td className="px-4 py-3 font-semibold">₹{card.amount}</td>
                    <td className="px-4 py-3 text-green-600 font-medium">
                      ₹{paidAmount}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`font-medium ${
                          pendingAmount === 0
                            ? "text-green-600"
                            : pendingAmount === card.amount
                            ? "text-red-600"
                            : "text-yellow-600"
                        }`}
                      >
                        ₹{pendingAmount}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div>{card.contact1}</div>
                      <div>{card.contact2 || "-"}</div>
                    </td>
                    <td className="px-4 py-3">{card.address}</td>
                    <td className="px-4 py-3 text-center flex gap-2 justify-center">
                      <button
                        onClick={() => handleEdit(card)}
                        className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded-md"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(card._id)}
                        className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded-md"
                      >
                        Delete
                      </button>
                      <button
                        onClick={() => navigate(`/dashboard/idcard-transactions/${card._id}`, { state: { idCard: card } })}
                        className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-md"
                      >
                        Manage 
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="9" className="text-center py-4 text-gray-500">
                  No ID Cards found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-lg rounded-lg shadow-lg p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4">
              {editingId ? "Edit ID Card" : "Create New ID Card"}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-3">
              {/* Checkbox for existing school */}
              <div className="flex items-center gap-2 mb-3">
                <input
                  type="checkbox"
                  id="useExistingSchool"
                  checked={useExistingSchool}
                  onChange={(e) => {
                    setUseExistingSchool(e.target.checked);
                    if (!e.target.checked) {
                      setSelectedSchoolId("");
                      setForm({
                        schoolName: "",
                        contact1: "",
                        contact2: "",
                        projectName: "",
                        amount: "",
                        address: "",
                      });
                    }
                  }}
                  className="w-4 h-4"
                />
                <label htmlFor="useExistingSchool" className="text-sm font-medium text-gray-700">
                  Use Existing School
                </label>
              </div>

              {/* School dropdown - only show if checkbox is checked */}
              {useExistingSchool && (
                <div className="mb-3">
                  <select
                    value={selectedSchoolId}
                    onChange={handleSchoolSelect}
                    className="w-full border px-3 py-2 rounded-md"
                    required={useExistingSchool}
                  >
                    <option value="">Select a school...</option>
                    {schools.map((school) => (
                      <option key={school._id} value={school._id}>
                        {school.schoolName} - {school.projectName}
                      </option>
                    ))}
                  </select>
                  {selectedSchoolId && (
                    <p className="text-xs text-blue-600 mt-1">
                      ℹ️ School details pre-filled. You can modify any field as needed.
                    </p>
                  )}
                </div>
              )}

              {/* Form fields - Always editable */}
              <input
                type="text"
                name="schoolName"
                value={form.schoolName}
                onChange={handleChange}
                placeholder="School Name"
                required
                className="w-full border px-3 py-2 rounded-md focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="text"
                name="contact1"
                value={form.contact1}
                onChange={handleChange}
                placeholder="Contact 1"
                required
                className="w-full border px-3 py-2 rounded-md focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="text"
                name="contact2"
                value={form.contact2}
                onChange={handleChange}
                placeholder="Contact 2 (Optional)"
                className="w-full border px-3 py-2 rounded-md focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="text"
                name="projectName"
                value={form.projectName}
                onChange={handleChange}
                placeholder="Project Name"
                required
                className="w-full border px-3 py-2 rounded-md focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="number"
                name="amount"
                value={form.amount}
                onChange={handleChange}
                placeholder="Amount"
                required
                className="w-full border px-3 py-2 rounded-md focus:ring-2 focus:ring-blue-500"
              />
              <textarea
                name="address"
                value={form.address}
                onChange={handleChange}
                placeholder="Address"
                required
                className="w-full border px-3 py-2 rounded-md focus:ring-2 focus:ring-blue-500"
              ></textarea>

              <div className="flex justify-end gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {editingId ? "Update" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}